import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, RefreshCw, Clock, ChevronDown, ChevronUp, AlertCircle, History, Zap, Settings, Trash2, Plus, X, ArrowUpDown, Trophy, TrendingUp } from 'lucide-react';
import styled, { keyframes, css } from 'styled-components';
import apiService from '../../services/apiService';
import { isMarketClosed } from '../../utils/marketCalendar';

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const pulse  = keyframes`0%,100%{opacity:1}50%{opacity:.4}`;
const glow   = keyframes`0%,100%{box-shadow:0 0 6px #a855f7}50%{box-shadow:0 0 18px #a855f7,0 0 30px #7e22ce}`;
const glowOrange = keyframes`0%,100%{box-shadow:0 0 6px #f97316}50%{box-shadow:0 0 18px #f97316,0 0 30px #ea580c}`;

const PageWrap = styled.div`
  min-height:100vh;background:#0a0d12;color:#e2e8f0;font-family:'Inter',sans-serif;padding:24px;
  @media(max-width:480px){padding:12px}
`;
const Header = styled.div`
  display:flex;justify-content:space-between;align-items:center;margin-bottom:28px;flex-wrap:wrap;gap:12px;
`;
const Title = styled.h1`
  font-size:24px;font-weight:700;color:#fff;display:flex;align-items:center;gap:10px;
  .badge{font-size:11px;background:linear-gradient(135deg,#f97316,#ea580c);padding:3px 8px;border-radius:12px;font-weight:600;letter-spacing:.5px;}
  .mbadge{font-size:10px;background:rgba(249,115,22,.15);color:#fb923c;border:1px solid rgba(249,115,22,.3);padding:2px 7px;border-radius:10px;font-weight:600;}
  @media(max-width:480px){font-size:18px;gap:6px;.badge{font-size:9px;padding:2px 6px}}
`;
const Controls = styled.div`display:flex;gap:10px;align-items:center;flex-wrap:wrap;`;
const Btn = styled.button`
  display:flex;align-items:center;gap:6px;padding:9px 16px;border-radius:8px;border:none;
  cursor:pointer;font-size:13px;font-weight:600;transition:all .2s;
  ${p => p.primary ? css`background:linear-gradient(135deg,#f97316,#ea580c);color:#fff;&:hover{opacity:.85}`
    : css`background:rgba(255,255,255,.06);color:#94a3b8;border:1px solid rgba(255,255,255,.1);&:hover{background:rgba(255,255,255,.1);color:#e2e8f0}`}
`;
const MarketClosedBadge = styled.div`
  display:flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;
  background:rgba(234,179,8,.08);border:1px solid rgba(234,179,8,.25);color:#eab308;font-size:12px;font-weight:600;
`;
const LiveDot = styled.div`
  width:8px;height:8px;border-radius:50%;background:#f97316;
  animation:${glowOrange} 2s ease-in-out infinite;
`;
const StatusBar = styled.div`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:10px;
  padding:12px 16px;margin-bottom:20px;display:flex;align-items:center;gap:12px;font-size:13px;color:#64748b;flex-wrap:wrap;
`;
const InfoTag = styled.span`
  font-size:10px;padding:2px 6px;border-radius:4px;
  background:rgba(249,115,22,.12);color:#fb923c;border:1px solid rgba(249,115,22,.2);
`;
const MomentumTag = styled.span`
  font-size:10px;padding:2px 6px;border-radius:4px;
  background:rgba(249,115,22,.2);color:#f97316;border:1px solid rgba(249,115,22,.4);font-weight:700;
`;
const FlatBaseTag = styled.span`
  font-size:10px;padding:2px 6px;border-radius:4px;
  background:rgba(34,197,94,.12);color:#4ade80;border:1px solid rgba(34,197,94,.25);font-weight:700;
`;

const SortBar = styled.div`
  display:flex;gap:8px;margin-bottom:14px;align-items:center;flex-wrap:wrap;
  font-size:12px;color:#64748b;
  @media(max-width:480px){gap:4px;font-size:11px}
`;
const SortBtn = styled.button`
  padding:5px 10px;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;
  border:1px solid ${p => p.active ? 'rgba(249,115,22,.4)' : 'rgba(255,255,255,.1)'};
  background:${p => p.active ? 'rgba(249,115,22,.15)' : 'rgba(255,255,255,.03)'};
  color:${p => p.active ? '#fb923c' : '#94a3b8'};
  &:hover{background:rgba(249,115,22,.1);color:#fb923c;}
`;
const Card = styled.div`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);border-radius:14px;
  animation:${fadeIn} .4s ease;overflow:hidden;
`;
const TableWrapper = styled.div`width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;`;

// V42: 9열 (V40 8열 + 30D 컬럼)
const GRID = '40px 150px 62px 62px 62px 55px 52px 108px 30px';
const TableHead = styled.div`
  display:grid;grid-template-columns:${GRID};
  gap:8px;padding:12px 16px;font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.5px;
  border-bottom:1px solid rgba(255,255,255,.06);font-weight:600;min-width:700px;
`;
const Row = styled.div`
  display:grid;grid-template-columns:${GRID};
  gap:8px;padding:12px 16px;font-size:13px;align-items:start;
  border-bottom:1px solid rgba(255,255,255,.04);cursor:pointer;transition:background .15s;min-width:700px;
  &:hover{background:rgba(249,115,22,.04);}
  &:last-child{border-bottom:none;}
  ${p => p.$momentum && css`border-left:2px solid rgba(249,115,22,.5);`}
`;
const RankBadge = styled.div`
  font-weight:700;font-size:14px;text-align:center;
  color:${p => p.$rank <= 3 ? '#fbbf24' : p.$rank <= 10 ? '#fb923c' : '#64748b'};
`;
const NameCell = styled.div`
  display:flex;flex-direction:column;gap:4px;min-width:0;
  .name{font-weight:600;color:#e2e8f0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .meta{font-size:11px;color:#475569;display:flex;gap:6px;align-items:center;flex-wrap:wrap;}
`;
const ScoreNum = styled.div`
  font-weight:700;text-align:right;
  color:${p => p.$score >= 70 ? '#fbbf24' : p.$score >= 50 ? '#fb923c' : p.$score >= 30 ? '#94a3b8' : '#475569'};
  font-size:${p => p.$big ? '15px' : '13px'};
`;
const SubScore = styled.div`text-align:right;color:#94a3b8;font-size:12px;`;
const PassPill = styled.div`
  display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:5px;font-size:11px;font-weight:600;
  ${p => p.$full ? css`background:rgba(34,197,94,.15);color:#4ade80;border:1px solid rgba(34,197,94,.25)`
    : p.$high ? css`background:rgba(249,115,22,.12);color:#fb923c;border:1px solid rgba(249,115,22,.2)`
    : p.$mid  ? css`background:rgba(234,179,8,.10);color:#eab308;border:1px solid rgba(234,179,8,.18)`
    : css`background:rgba(255,255,255,.04);color:#64748b;border:1px solid rgba(255,255,255,.08)`}
`;

const VERDICT_STYLE = {
    BREAKOUT:          css`background:rgba(239,68,68,.15);color:#ef4444;border:1px solid rgba(239,68,68,.25)`,
    NEAR_BREAKOUT:     css`background:rgba(234,179,8,.12);color:#eab308;border:1px solid rgba(234,179,8,.2)`,
    FORMING_BASE:      css`background:rgba(99,102,241,.1);color:#818cf8;border:1px solid rgba(99,102,241,.2)`,
    MOMENTUM_SURGE:    css`background:rgba(249,115,22,.2);color:#f97316;border:1px solid rgba(249,115,22,.4)`,
    MOMENTUM_BREAKOUT: css`background:rgba(234,179,8,.15);color:#eab308;border:1px solid rgba(234,179,8,.3)`,
    REJECTED:          css`background:rgba(255,255,255,.04);color:#64748b;border:1px solid rgba(255,255,255,.08)`,
};
const VerdictBadge = styled.span`
  font-size:10px;font-weight:700;padding:2px 6px;border-radius:4px;letter-spacing:.3px;
  ${p => VERDICT_STYLE[p.$v] || VERDICT_STYLE.REJECTED}
`;

const Detail = styled.div`
  background:rgba(0,0,0,.25);padding:18px 24px;border-bottom:1px solid rgba(249,115,22,.12);
  border-top:1px solid rgba(249,115,22,.08);animation:${fadeIn} .3s ease;
`;
const ComponentRow = styled.div`
  display:grid;grid-template-columns:130px 1fr 80px 80px;gap:12px;align-items:center;
  padding:6px 0;font-size:12px;
`;
const Bar = styled.div`height:8px;background:rgba(255,255,255,.04);border-radius:4px;overflow:hidden;`;
const BarFill = styled.div`
  height:100%;width:${p => p.pct}%;border-radius:4px;
  background:${p => p.$momentum
    ? 'linear-gradient(90deg,#f97316,#ea580c)'
    : p.pass
      ? 'linear-gradient(90deg,#a855f7,#7e22ce)'
      : 'linear-gradient(90deg,#64748b,#475569)'};
  transition:width .4s ease;
`;
const SectionLabel = styled.div`
  font-size:11px;font-weight:700;color:#f97316;text-transform:uppercase;letter-spacing:.5px;
  margin:14px 0 6px;padding:4px 8px;background:rgba(249,115,22,.08);border-radius:4px;
  border-left:3px solid #f97316;
`;
const Empty    = styled.div`text-align:center;padding:40px;color:#334155;font-size:14px;`;
const LoadingDot = styled.div`animation:${pulse} 1.4s ease-in-out infinite;text-align:center;padding:40px;color:#f97316;font-size:28px;`;
const ModalOverlay = styled.div`
  position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.85);
  display:flex;align-items:center;justify-content:center;z-index:1000;backdrop-filter:blur(5px);
`;
const Modal = styled.div`
  background:#0a0d12;border:1px solid rgba(255,255,255,.1);border-radius:16px;
  width:90%;max-width:400px;padding:24px;color:#fff;
`;

const COMPONENT_LABELS = {
    // Flat Base 트랙
    proximity:     '저항선근접',
    volatility:    '변동성(Flat)',
    volSurge:      '거래량폭발',
    vcp:           'VCP수렴',
    uptrend:       '정배열',
    closePosition: '종가위치',
    trendStarter:  '추세초입',
    megaVol:       '대형거래대금',
    breakoutDay:   '돌파일보너스',
    megaCapBreakout: '대형주ATH',
    // Momentum 트랙
    acceleration:      '⚡가속도(30D)',
    momentumVolSurge:  '⚡모멘텀거래량',
    trValueScore:      '⚡거래대금절대값',
};

const formatComponentValue = (key, c) => {
    if (!c) return '';
    if (key === 'proximity')     return `${((c.value||0)*100).toFixed(1)}% (>${((c.threshold||0)*100).toFixed(0)}%)`;
    if (key === 'volatility')    return `${((c.value||0)*100).toFixed(1)}% (<${((c.threshold||0)*100).toFixed(0)}%)`;
    if (key === 'volSurge')      return `${(c.value||0).toFixed(2)}x (>${(c.threshold||0).toFixed(1)}x)`;
    if (key === 'vcp')           return `tail ${(c.tailRatio||0).toFixed(2)}x · dried ${c.driedDays??'?'}일`;
    if (key === 'uptrend')       return `MA20:${c.ma20??'-'} 60:${c.ma60??'-'} 120:${c.ma120??'-'}`;
    if (key === 'closePosition') return `${((c.value||0)*100).toFixed(0)}% (>${((c.threshold||0)*100).toFixed(0)}%)`;
    if (key === 'trendStarter')  return c.active ? '활성' : '미활성';
    if (key === 'megaVol') {
        const t = c.trValue || 0;
        return t >= 1_000_000_000_000
            ? `${(t/1_000_000_000_000).toFixed(1)}조`
            : `${Math.floor(t/100_000_000)}억`;
    }
    if (key === 'breakoutDay')   return `${(c.volSurge||0).toFixed(2)}x · 근접 ${c.proximityRatio!=null?(c.proximityRatio*100).toFixed(1):'-'}%`;
    if (key === 'megaCapBreakout') return c.isNew52WHigh ? '52W신고가✅' : '52W신고가❌';
    if (key === 'acceleration')  return `30D ${((c.priceChange30D||0)*100).toFixed(1)}%`;
    if (key === 'momentumVolSurge') return `거래량 ${(c.volSurge||0).toFixed(2)}x`;
    if (key === 'trValueScore') {
        const t = c.trValue || 0;
        return t >= 1_000_000_000_000
            ? `${(t/1_000_000_000_000).toFixed(1)}조`
            : `${Math.floor(t/100_000_000)}억`;
    }
    return '';
};

const MOMENTUM_KEYS = new Set(['acceleration', 'momentumVolSurge', 'trValueScore']);
const FLATBASE_KEYS = new Set(['volatility', 'volSurge', 'vcp', 'trendStarter']);

export default function LivermoreV42Page() {
    const navigate = useNavigate();
    const [data, setData]         = useState(null);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [marketClosed] = useState(isMarketClosed(new Date()));
    const [expanded, setExpanded] = useState({});
    const [sortKey, setSortKey]   = useState('total');

    const [schedulerConfig, setSchedulerConfig] = useState({
        enabled: true, times: ['09:30', '13:00', '15:00'], weekdaysOnly: true
    });
    const [showSettings, setShowSettings] = useState(false);
    const [newTime, setNewTime]   = useState('09:00');
    const isExecutingRef = useRef(false);

    const load = useCallback(async (force = false) => {
        setLoading(true); setError(null);
        try {
            const res = await apiService.getLivermoreV42Leaders(force);
            setData(res);
            setLastUpdate(new Date());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchScheduler = useCallback(async () => {
        try {
            const res = await apiService.getLivermoreV42Scheduler();
            if (res?.config) setSchedulerConfig(res.config);
        } catch (e) { console.error('V42 scheduler fetch error', e); }
    }, []);

    useEffect(() => { load(); fetchScheduler(); }, [load, fetchScheduler]);

    const runAutoSaveProcedure = useCallback(async (config) => {
        if (!config?.enabled || !config.times?.length) return;
        const now = new Date();
        const kstNow  = new Date(now.getTime() + 9 * 60 * 60 * 1000);
        const todayStr  = kstNow.toISOString().split('T')[0];
        const currentMins = now.getHours() * 60 + now.getMinutes();
        const storageKey  = 'livermore_v42_processed_slots';

        try {
            for (const timeStr of config.times) {
                const [sh, sm] = timeStr.split(':').map(Number);
                const slotMins = sh * 60 + sm;
                if (currentMins < slotMins || currentMins >= slotMins + 30) continue;

                let processedData = { date: '', slots: {} };
                try {
                    const stored = localStorage.getItem(storageKey);
                    if (stored) processedData = JSON.parse(stored);
                } catch (_) {}

                if (processedData.date === todayStr && processedData.slots[timeStr]) continue;
                if (isExecutingRef.current) return;

                console.log(`[V42 Scheduler] Triggering check for ${timeStr}...`);
                isExecutingRef.current = true;

                const history = await apiService.getLivermoreV42History();
                const alreadyExists = (history.items || []).some(h => {
                    const hDate     = new Date(h.createdAt);
                    const hTimeMins = hDate.getHours() * 60 + hDate.getMinutes();
                    const hKst      = new Date(hDate.getTime() + 9 * 60 * 60 * 1000);
                    return hKst.toISOString().split('T')[0] === todayStr && Math.abs(hTimeMins - slotMins) < 15;
                });

                if (!alreadyExists) {
                    console.log(`[V42 Scheduler] Saving snapshot for ${timeStr}...`);
                    await apiService.saveLivermoreV42Snapshot();
                    await load();
                }

                processedData.date = todayStr;
                processedData.slots[timeStr] = true;
                localStorage.setItem(storageKey, JSON.stringify(processedData));
                isExecutingRef.current = false;
            }
        } catch (e) {
            console.error('[V42 Scheduler] Error:', e);
            isExecutingRef.current = false;
        }
    }, [load]);

    useEffect(() => {
        if (!schedulerConfig?.enabled) return;
        const id = setInterval(() => runAutoSaveProcedure(schedulerConfig), 10000);
        return () => clearInterval(id);
    }, [schedulerConfig, runAutoSaveProcedure]);

    const handleUpdateScheduler = async (updates) => {
        try {
            const res = await apiService.updateLivermoreV42Scheduler({ ...schedulerConfig, ...updates });
            if (res.success) setSchedulerConfig(res.status.config);
        } catch (err) { alert(`스케줄러 업데이트 실패: ${err.message}`); }
    };

    const saveSnapshot = async () => {
        try {
            setLoading(true);
            await apiService.saveLivermoreV42Snapshot();
            await load(true);
        } catch (e) { setError(e.message); setLoading(false); }
    };

    const sortedRanked = useMemo(() => {
        if (!data?.ranked) return [];
        const arr = [...data.ranked];
        if      (sortKey === 'total')      arr.sort((a, b) => b.totalScore - a.totalScore);
        else if (sortKey === 'pivot')      arr.sort((a, b) => (b.effectivePivotScore||b.pivotScore) - (a.effectivePivotScore||a.pivotScore));
        else if (sortKey === 'leader')     arr.sort((a, b) => (b.leaderScore||0) - (a.leaderScore||0));
        else if (sortKey === 'thresholds') arr.sort((a, b) => (b.thresholdsPassed||0) - (a.thresholdsPassed||0));
        else if (sortKey === 'momentum')   arr.sort((a, b) => {
            if (a.isMomentumStock && !b.isMomentumStock) return -1;
            if (!a.isMomentumStock && b.isMomentumStock) return 1;
            return b.totalScore - a.totalScore;
        });
        return arr;
    }, [data, sortKey]);

    const toggle = (code) => setExpanded(prev => ({ ...prev, [code]: !prev[code] }));

    const moodBadge = () => {
        const mood = data?.marketMood;
        if (mood === 'MOMENTUM') return { bg: 'rgba(249,115,22,.15)', color: '#f97316', border: 'rgba(249,115,22,.35)', label: '⚡ MOMENTUM' };
        if (mood === 'HOT')      return { bg: 'rgba(168,85,247,.12)', color: '#d8b4fe', border: 'rgba(168,85,247,.25)', label: '🔥 HOT' };
        return { bg: 'rgba(100,116,139,.08)', color: '#94a3b8', border: 'rgba(100,116,139,.2)', label: '😐 NEUTRAL' };
    };

    return (
        <PageWrap>
            <Header>
                <Title>
                    <Trophy size={22} color="#f97316" />
                    리버모어 스코어링
                    <span className="badge">V4.2</span>
                    <span className="mbadge">⚡ Momentum</span>
                </Title>
                <Controls>
                    {marketClosed && (
                        <MarketClosedBadge><AlertCircle size={14} />MARKET CLOSED</MarketClosedBadge>
                    )}
                    {data?.marketMood && (() => {
                        const mb = moodBadge();
                        return (
                            <div style={{ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 6,
                                background: mb.bg, color: mb.color, border: `1px solid ${mb.border}` }}>
                                {mb.label}
                            </div>
                        );
                    })()}
                    <Btn onClick={() => setShowSettings(true)}>
                        <Settings size={14} />설정
                        {schedulerConfig?.enabled && (
                            <span style={{ width:6, height:6, background:'#f97316', borderRadius:'50%', marginLeft:4, boxShadow:'0 0 8px #f97316' }} />
                        )}
                    </Btn>
                    <Btn onClick={() => navigate('/labs/livermore-v42/history')}>
                        <History size={14} />히스토리
                    </Btn>
                    <Btn onClick={() => load(true)}>
                        <RefreshCw size={14} />새로고침
                    </Btn>
                    <Btn primary onClick={saveSnapshot}>
                        <Zap size={14} />스냅샷 저장
                    </Btn>
                </Controls>
            </Header>

            <StatusBar>
                <LiveDot />
                <span style={{ color: '#94a3b8' }}>V4.2 Dual Track:</span>
                <InfoTag>Flat Base + Momentum</InfoTag>
                <InfoTag>Verdict 6종</InfoTag>
                {data?.ranked && <InfoTag>분석 {data.ranked.length}개</InfoTag>}
                {(data?.momentumCount ?? 0) > 0 && (
                    <MomentumTag>⚡ 모멘텀 {data.momentumCount}개</MomentumTag>
                )}
                {(data?.flatBaseCount ?? 0) > 0 && (
                    <FlatBaseTag>📐 베이스 {data.flatBaseCount}개</FlatBaseTag>
                )}
                {lastUpdate && (
                    <span style={{ marginLeft: 'auto', color: '#475569', fontSize: '12px' }}>
                        <Clock size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        {lastUpdate.toLocaleTimeString('ko-KR')}
                    </span>
                )}
            </StatusBar>

            {/* 스케줄러 설정 모달 */}
            {showSettings && schedulerConfig && (
                <ModalOverlay onClick={() => setShowSettings(false)}>
                    <Modal onClick={e => e.stopPropagation()}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
                            <h2 style={{ fontSize:'18px', margin:0, display:'flex', alignItems:'center', gap:8 }}>
                                <Clock size={18} color="#f97316" /> 스케줄러 설정
                            </h2>
                            <button onClick={() => setShowSettings(false)} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer' }}>
                                <X size={20} />
                            </button>
                        </div>
                        <div style={{ marginBottom:24 }}>
                            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:'14px' }}>
                                <input type="checkbox" checked={schedulerConfig.enabled}
                                    onChange={e => handleUpdateScheduler({ enabled: e.target.checked })} />
                                자동 분석 및 스냅샷 저장 켜기
                            </label>
                        </div>
                        <div style={{ marginBottom:12, fontSize:'12px', color:'#64748b', textTransform:'uppercase' }}>실행 시간 목록</div>
                        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            {schedulerConfig.times.map(time => (
                                <div key={time} style={{ display:'flex', justifyContent:'space-between', background:'rgba(255,255,255,.03)', padding:'10px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,.05)' }}>
                                    <span>{time}</span>
                                    <button onClick={() => handleUpdateScheduler({ times: schedulerConfig.times.filter(t => t !== time) })}
                                        style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer' }}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop:20, display:'flex', gap:10 }}>
                            <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)}
                                style={{ flex:1, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', color:'#fff', padding:'10px', borderRadius:8 }} />
                            <button onClick={() => {
                                if (!schedulerConfig.times.includes(newTime)) {
                                    handleUpdateScheduler({ times: [...schedulerConfig.times, newTime].sort() });
                                }
                            }} style={{ background:'#f97316', color:'#0a0d12', border:'none', padding:'10px 16px', borderRadius:8, fontWeight:700, cursor:'pointer' }}>
                                <Plus size={16} />
                            </button>
                        </div>
                    </Modal>
                </ModalOverlay>
            )}

            {error && (
                <Card style={{ borderColor:'rgba(239,68,68,.2)', marginBottom:20, padding:16 }}>
                    <span style={{ color:'#ef4444' }}>⚠️ {error}</span>
                </Card>
            )}

            <SortBar>
                <ArrowUpDown size={13} /> 정렬:
                <SortBtn active={sortKey === 'total'}      onClick={() => setSortKey('total')}>총점</SortBtn>
                <SortBtn active={sortKey === 'pivot'}      onClick={() => setSortKey('pivot')}>Pivot</SortBtn>
                <SortBtn active={sortKey === 'leader'}     onClick={() => setSortKey('leader')}>Leader</SortBtn>
                <SortBtn active={sortKey === 'thresholds'} onClick={() => setSortKey('thresholds')}>기준치 통과</SortBtn>
                <SortBtn active={sortKey === 'momentum'}   onClick={() => setSortKey('momentum')}>⚡ 모멘텀 우선</SortBtn>
            </SortBar>

            {loading ? (
                <LoadingDot>⚡ 분석 중...</LoadingDot>
            ) : !data?.ranked || data.ranked.length === 0 ? (
                <Card><Empty>분석된 종목이 없습니다</Empty></Card>
            ) : (
                <Card>
                    <TableWrapper>
                        <TableHead>
                            <div style={{ textAlign:'center' }}>#</div>
                            <div>종목</div>
                            <div style={{ textAlign:'right' }}>총점</div>
                            <div style={{ textAlign:'right' }}>Leader</div>
                            <div style={{ textAlign:'right' }}>Pivot</div>
                            <div style={{ textAlign:'center' }}>통과</div>
                            <div style={{ textAlign:'center' }}>30D</div>
                            <div style={{ textAlign:'center' }}>V42 판정</div>
                            <div></div>
                        </TableHead>

                        {sortedRanked.map((s) => {
                            const isOpen    = !!expanded[s.code];
                            const passRatio = (s.thresholdsPassed||0) / (s.thresholdsTotal||7);
                            const isMomentum = s.isMomentumStock;
                            const change30D  = s.priceChange30DRaw ?? 0;
                            const effectivePivot = s.effectivePivotScore ?? s.pivotScore;

                            return (
                                <React.Fragment key={s.code}>
                                    <Row onClick={() => toggle(s.code)} $momentum={isMomentum}>
                                        <RankBadge $rank={s.rank}>{s.rank}</RankBadge>
                                        <NameCell>
                                            <div className="name">{s.name}</div>
                                            <div className="meta">
                                                <span>{s.price?.toLocaleString()}원</span>
                                                <span style={{ color: s.changeRate >= 0 ? '#ef4444' : '#3b82f6' }}>
                                                    {s.changeRate >= 0 ? '+' : ''}{s.changeRate?.toFixed(2)}%
                                                </span>
                                                {isMomentum && (
                                                    <span style={{ background:'linear-gradient(135deg,#f97316,#ea580c)', color:'#fff', padding:'1px 5px', borderRadius:'3px', fontSize:'9px', fontWeight:700 }}>
                                                        ⚡모멘텀
                                                    </span>
                                                )}
                                                {s.isTrendStarter && !isMomentum && (
                                                    <span style={{ background:'linear-gradient(135deg,#10b981,#059669)', color:'#fff', padding:'1px 5px', borderRadius:'3px', fontSize:'9px', fontWeight:700 }}>
                                                        👑추세초입
                                                    </span>
                                                )}
                                            </div>
                                        </NameCell>
                                        <ScoreNum $big $score={s.totalScore}>{s.totalScore?.toFixed(1)}</ScoreNum>
                                        <SubScore>{s.leaderScore?.toFixed(1)}</SubScore>
                                        <SubScore>{effectivePivot?.toFixed(1)}</SubScore>
                                        <div style={{ textAlign:'center' }}>
                                            <PassPill $full={passRatio >= 1} $high={passRatio >= 0.7 && passRatio < 1} $mid={passRatio >= 0.4 && passRatio < 0.7}>
                                                {s.thresholdsPassed}/{s.thresholdsTotal}
                                            </PassPill>
                                        </div>
                                        <div style={{ textAlign:'center', fontSize:11, fontWeight:700,
                                            color: change30D >= 0.5 ? '#f97316' : change30D >= 0.15 ? '#eab308' : change30D >= 0 ? '#94a3b8' : '#ef4444' }}>
                                            {s.priceChange30D || '-'}
                                        </div>
                                        <div style={{ textAlign:'center' }}>
                                            <VerdictBadge $v={s.v31Verdict}>{s.v31Verdict?.replace('_', ' ')}</VerdictBadge>
                                        </div>
                                        <div style={{ display:'flex', gap:6, justifyContent:'center', alignItems:'center', color:'#64748b' }}>
                                            <button onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/labs/stock/technical?symbol=${s.code}${s.resistanceLine ? `&pivot=${s.resistanceLine}` : ''}`);
                                            }} style={{ background:'none', border:'none', cursor:'pointer', color:'#f97316', display:'flex', alignItems:'center', padding:4 }} title="차트 보기">
                                                <TrendingUp size={14} />
                                            </button>
                                            {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                        </div>
                                    </Row>

                                    {isOpen && s.components && (
                                        <Detail>
                                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, fontSize:12, color:'#64748b' }}>
                                                <span>
                                                    {isMomentum
                                                        ? `⚡ Momentum Track — Pivot ${effectivePivot?.toFixed(1)} (30D: ${s.priceChange30D})`
                                                        : `Flat Base Track — Pivot ${effectivePivot?.toFixed(1)} / 100`
                                                    }
                                                </span>
                                                <span style={{ color:'#f97316', cursor:'pointer', fontWeight:600 }}
                                                    onClick={(e) => { e.stopPropagation(); window.open(`/labs/stock/technical?symbol=${s.code}${s.resistanceLine ? `&pivot=${s.resistanceLine}` : ''}`, '_blank'); }}>
                                                    차트 보기 →
                                                </span>
                                            </div>

                                            {/* 모멘텀 종목: Momentum Track 섹션 먼저 표시 */}
                                            {isMomentum && (
                                                <>
                                                    <SectionLabel>⚡ Momentum Track (실제 적용)</SectionLabel>
                                                    {['acceleration', 'momentumVolSurge', 'trValueScore'].map(key => {
                                                        const c = s.components[key];
                                                        if (!c) return null;
                                                        const pct = Math.min((c.score / c.max) * 100, 100);
                                                        return (
                                                            <ComponentRow key={key}>
                                                                <div style={{ color: c.score > 0 ? '#fb923c' : '#64748b', fontWeight:600 }}>
                                                                    {c.score > 0 ? '⚡' : '○'} {COMPONENT_LABELS[key]}
                                                                </div>
                                                                <Bar><BarFill pct={pct} $momentum={true} pass={true} /></Bar>
                                                                <div style={{ textAlign:'right', color:'#fb923c', fontWeight:600 }}>
                                                                    {c.score.toFixed(1)} / {c.max}
                                                                </div>
                                                                <div style={{ textAlign:'right', color:'#475569', fontSize:11 }}>
                                                                    {formatComponentValue(key, c)}
                                                                </div>
                                                            </ComponentRow>
                                                        );
                                                    })}
                                                    <SectionLabel style={{ background:'rgba(100,116,139,.06)', color:'#64748b', borderColor:'#64748b' }}>
                                                        공통 항목
                                                    </SectionLabel>
                                                </>
                                            )}

                                            {/* 공통 항목 및 Flat Base 트랙 */}
                                            {Object.entries(s.components)
                                                .filter(([key]) => !MOMENTUM_KEYS.has(key) && !(isMomentum && FLATBASE_KEYS.has(key)))
                                                .map(([key, c]) => {
                                                    const pct = Math.min((c.score / c.max) * 100, 100);
                                                    return (
                                                        <ComponentRow key={key}>
                                                            <div style={{ color: c.pass ? '#d8b4fe' : '#64748b', fontWeight:600 }}>
                                                                {c.pass ? '✅' : '❌'} {COMPONENT_LABELS[key] || key}
                                                            </div>
                                                            <Bar><BarFill pct={pct} pass={c.pass} /></Bar>
                                                            <div style={{ textAlign:'right', color:'#94a3b8', fontWeight:600 }}>
                                                                {c.score.toFixed(1)} / {c.max}
                                                            </div>
                                                            <div style={{ textAlign:'right', color:'#475569', fontSize:11 }}>
                                                                {formatComponentValue(key, c)}
                                                            </div>
                                                        </ComponentRow>
                                                    );
                                                })}
                                        </Detail>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableWrapper>
                </Card>
            )}
        </PageWrap>
    );
}
